import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { TrophyIcon } from "lucide-react";
import { useId } from "react";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";

import { type Award, type AwardKind } from "../../model/awards";
import type { FriendsPeriod } from "../../model/FRIENDS_PERIODS";
import { formatNumber } from "../../model/formatMetric";

const TITLES: Record<AwardKind, MessageDescriptor> = {
  marathon: msg`Marathoner`,
  farmer: msg`Farmer`,
  immortal: msg`Immortal`,
  sponsor: msg`Enemy sponsor`,
  riser: msg`Breakthrough`,
};

const NOTES: Record<AwardKind, MessageDescriptor> = {
  marathon: msg`Played more than anyone.`,
  farmer: msg`Most souls per minute of play.`,
  immortal: msg`Gives the enemy souls least often.`,
  sponsor: msg`Dies more often than anyone.`,
  riser: msg`Raised the overall score the most compared with the previous period.`,
};

const ICON_COLORS: Record<AwardKind, string> = {
  marathon: "text-accent",
  farmer: "text-accent",
  immortal: "text-good",
  sponsor: "text-bad",
  riser: "text-rival",
};

const MATCHES = msg({ message: "{count, plural, one {# match} other {# matches}}" });
const SOULS = msg({ message: "{value} souls per minute" });
const DEATHS = msg({ message: "{value} deaths per match" });
const RISE = msg({ message: "score +{value}" });

const PERIOD_TITLES: Record<FriendsPeriod, MessageDescriptor> = {
  week: msg`Awards of the week`,
  month: msg`Awards of the month`,
  all: msg`All-time awards`,
};

type AwardsSectionProps = {
  awards: readonly Award[];
  period: FriendsPeriod;
  meId: number;
};

// Номинации периода: кто больше всех играл, фармил, реже и чаще всех умирал, сильнее всех вырос.
export const AwardsSection = ({ awards, period, meId }: AwardsSectionProps) => {
  const { i18n, t } = useLingui();
  const heroLookup = useHeroLookup();
  const headingId = useId();
  const valueText = (award: Award) => {
    switch (award.kind) {
      case "marathon":
        return i18n._({ ...MATCHES, values: { count: award.value } });
      case "farmer":
        return i18n._({ ...SOULS, values: { value: formatNumber(award.value, i18n.locale) } });
      case "immortal":
      case "sponsor":
        return i18n._({ ...DEATHS, values: { value: formatNumber(award.value, i18n.locale, 1) } });
      case "riser":
        return i18n._({ ...RISE, values: { value: award.value } });
    }
  };
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <h2 id={headingId} className="font-display text-[26px] leading-tight">
        {i18n._(PERIOD_TITLES[period])}
      </h2>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3.5">
        {awards.map((award) => {
          const isMe = award.player.account_id === meId;
          const hero =
            award.player.main_hero_id === null ? null : heroLookup(award.player.main_hero_id);
          return (
            <li
              key={award.kind}
              className={cn(
                "flex flex-col gap-2.5 rounded-[10px] border bg-surface px-4.5 py-4",
                isMe ? "border-border-strong" : "border-border",
              )}
            >
              <div className="flex items-center gap-2.5">
                <TrophyIcon aria-hidden="true" className={cn("size-5", ICON_COLORS[award.kind])} />
                <h3 className="font-semibold">{i18n._(TITLES[award.kind])}</h3>
              </div>
              <div className="flex items-center gap-2.5">
                {hero !== null && <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={36} />}
                <span className="flex flex-col">
                  <span className="font-semibold">
                    {isMe ? t`You` : (award.player.display_name ?? t`Friend`)}
                  </span>
                  <span className="font-mono text-[13px] text-muted">{valueText(award)}</span>
                </span>
              </div>
              <p className="text-[13px] text-subtle">{i18n._(NOTES[award.kind])}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
